import {
  boundaryTextAnchor,
  createSvgEl,
  getCartesianAxisLayout,
  type CartesianDomain,
  type CartesianPlotRect,
} from '@learnforge/demo-render';
import { formatTick, niceTickStep } from '@learnforge/demo-math/numeric';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import type { Projector } from './curve';

export interface CartesianPlaneOptions {
  domains: CartesianDomain;
  plotRect: CartesianPlotRect;
}

export interface CartesianPlaneHandle extends Projector {
  /** Clipped plot layer: append curves and other world-space content here. */
  readonly content: SVGGElement;
  screenToWorld(point: Vec2): Vec2;
  getDomains(): CartesianDomain;
  setDomains(domains: CartesianDomain): void;
  redraw(): void;
  destroy(): void;
}

const TARGET_TICKS = 10;
const TICK_HALF = 4;
const TICK_FONT = 10;
let clipId = 0;

function cloneDomains(domains: CartesianDomain): CartesianDomain {
  return {
    xDomain: [...domains.xDomain] as [number, number],
    yDomain: [...domains.yDomain] as [number, number],
  };
}

/** True when a finite world-space point lies inside both visible domains. */
export function isPointInCartesianDomain(point: Vec2, domains: CartesianDomain): boolean {
  return (
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    point.x >= domains.xDomain[0] &&
    point.x <= domains.xDomain[1] &&
    point.y >= domains.yDomain[0] &&
    point.y <= domains.yDomain[1]
  );
}

function tickValues(domain: [number, number], step: number): number[] {
  const epsilon = step * 1e-9;
  const first = Math.ceil((domain[0] - epsilon) / step) * step;
  const values: number[] = [];
  for (let value = first; value <= domain[1] + epsilon; value += step) {
    values.push(Number(value.toPrecision(14)));
  }
  return values;
}

/**
 * Creates a Cartesian drawing layer whose projector keeps the same object
 * identity while its visible world domains change.
 */
export function createCartesianPlane(
  svg: SVGSVGElement,
  options: CartesianPlaneOptions,
): CartesianPlaneHandle {
  let domains = cloneDomains(options.domains);
  const { plotRect } = options;
  const root = createSvgEl('g', { class: 'calc-cartesian-plane' });
  const defs = createSvgEl('defs');
  const id = `calc-cartesian-clip-${clipId++}`;
  const clipPath = createSvgEl('clipPath', { id });
  clipPath.appendChild(
    createSvgEl('rect', {
      x: plotRect.x,
      y: plotRect.y,
      width: plotRect.width,
      height: plotRect.height,
    }),
  );
  defs.appendChild(clipPath);

  const content = createSvgEl('g', {
    class: 'calc-cartesian-plane-content',
    'clip-path': `url(#${id})`,
  });
  const grid = createSvgEl('g', { class: 'calc-cartesian-plane-grid' });
  content.appendChild(grid);
  root.append(defs, content);
  svg.appendChild(root);

  let destroyed = false;

  function worldToScreen(point: Vec2): Vec2 {
    const [xMin, xMax] = domains.xDomain;
    const [yMin, yMax] = domains.yDomain;
    return {
      x: plotRect.x + ((point.x - xMin) / (xMax - xMin)) * plotRect.width,
      y: plotRect.y + (1 - (point.y - yMin) / (yMax - yMin)) * plotRect.height,
    };
  }

  function screenToWorld(point: Vec2): Vec2 {
    const [xMin, xMax] = domains.xDomain;
    const [yMin, yMax] = domains.yDomain;
    return {
      x: xMin + ((point.x - plotRect.x) / plotRect.width) * (xMax - xMin),
      y: yMin + (1 - (point.y - plotRect.y) / plotRect.height) * (yMax - yMin),
    };
  }

  function redraw(): void {
    if (destroyed) return;
    grid.replaceChildren();
    const [xMin, xMax] = domains.xDomain;
    const [yMin, yMax] = domains.yDomain;
    const xStep = niceTickStep(xMax - xMin, TARGET_TICKS);
    const yStep = niceTickStep(yMax - yMin, TARGET_TICKS);
    const axisLayout = getCartesianAxisLayout(domains.xDomain, domains.yDomain);
    const xAxisY = worldToScreen({ x: xMin, y: axisLayout.xAxis.value }).y;
    const yAxisX = worldToScreen({ x: axisLayout.yAxis.value, y: yMin }).x;

    for (const x of tickValues(domains.xDomain, xStep)) {
      const screenX = worldToScreen({ x, y: yMin }).x;
      grid.appendChild(
        createSvgEl('line', {
          x1: screenX,
          y1: plotRect.y,
          x2: screenX,
          y2: plotRect.y + plotRect.height,
          class: 'calc-cartesian-gridline',
        }),
      );
      if (Math.abs(x) > xStep * 1e-9) {
        grid.appendChild(
          createSvgEl('line', {
            x1: screenX,
            y1: xAxisY + axisLayout.xAxis.tickOffsets[0] * TICK_HALF,
            x2: screenX,
            y2: xAxisY + axisLayout.xAxis.tickOffsets[1] * TICK_HALF,
            class: 'calc-cartesian-tick',
          }),
        );
        const label = createSvgEl('text', {
          x: screenX,
          y: xAxisY + axisLayout.xAxis.labelDirection * (TICK_HALF + TICK_FONT),
          'text-anchor': boundaryTextAnchor(screenX, plotRect.x, plotRect.x + plotRect.width),
          class: 'calc-cartesian-tick-label',
          'data-axis': 'x',
        });
        label.textContent = formatTick(x, xStep);
        grid.appendChild(label);
      }
    }

    for (const y of tickValues(domains.yDomain, yStep)) {
      const screenY = worldToScreen({ x: xMin, y }).y;
      grid.appendChild(
        createSvgEl('line', {
          x1: plotRect.x,
          y1: screenY,
          x2: plotRect.x + plotRect.width,
          y2: screenY,
          class: 'calc-cartesian-gridline',
        }),
      );
      if (Math.abs(y) > yStep * 1e-9) {
        grid.appendChild(
          createSvgEl('line', {
            x1: yAxisX + axisLayout.yAxis.tickOffsets[0] * TICK_HALF,
            y1: screenY,
            x2: yAxisX + axisLayout.yAxis.tickOffsets[1] * TICK_HALF,
            y2: screenY,
            class: 'calc-cartesian-tick',
          }),
        );
        const label = createSvgEl('text', {
          x: yAxisX + axisLayout.yAxis.labelDirection * (TICK_HALF + 2),
          y: screenY + TICK_FONT / 2,
          'text-anchor': axisLayout.yAxis.labelAnchor,
          class: 'calc-cartesian-tick-label',
          'data-axis': 'y',
        });
        label.textContent = formatTick(y, yStep);
        grid.appendChild(label);
      }
    }

    grid.append(
      createSvgEl('line', {
        x1: plotRect.x,
        y1: xAxisY,
        x2: plotRect.x + plotRect.width,
        y2: xAxisY,
        class: 'calc-cartesian-axis',
      }),
      createSvgEl('line', {
        x1: yAxisX,
        y1: plotRect.y,
        x2: yAxisX,
        y2: plotRect.y + plotRect.height,
        class: 'calc-cartesian-axis',
      }),
    );
  }

  redraw();

  return {
    content,
    worldToScreen,
    screenToWorld,
    getDomains: () => cloneDomains(domains),
    setDomains(next: CartesianDomain): void {
      domains = cloneDomains(next);
      redraw();
    },
    redraw,
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      root.remove();
    },
  };
}
